/**
 * Maintenance page (Monday ticket 12906284771, Figma node 150:2189; Smartli's
 * own frame is 150:2284).
 *
 * Deliberately self-contained: no shared layout, no providers, no data
 * fetching. This renders when the API is unreachable, so anything it depends
 * on is something that can stop it rendering at exactly the wrong moment.
 * `logo.svg` is served from the app's own CDN root rather than imported, for
 * the same reason.
 */
import styled from "styled-components";

import { APP, SHARED } from "./content";

/**
 * Sampled from the Figma render (node 150:2189), not taken from the app theme.
 *
 * This matters: the design uses a NEUTRAL grey ramp, while the app's chrome
 * uses blue/purple-tinted greys (text #080E28, secondary #4B5B72,
 * textSecondary #8381A6). Substituting theme tokens here visibly shifts the
 * page — the body copy in particular goes several shades too dark and too
 * blue. Only the brand accent comes from the theme, because that is the value
 * that legitimately differs per app.
 *
 * Read off the rendered PNG rather than Figma's own tokens: the Figma seat is
 * a View seat and its MCP call limit is exhausted. Opacity is therefore not
 * recoverable — anything here may really be black at partial alpha, which is
 * identical over this page's white ground but would differ on another.
 */
const ink = {
  heading: APP.ink,
  muted: APP.muted,
  page: "#FFFFFF",
  /* The divider and card border are the app's own headline ink at 9% and 7%,
     not a shared neutral. That only looked neutral on AliDrop because its ink
     is #191919, i.e. rgb(25,25,25). Hex alpha: 17 = 9%, 12 = 7%. */
  divider: `${APP.ink}17`,
  cardBorder: `${APP.ink}12`,
  cardBg: "rgba(255, 255, 255, 0.8)",
  pillBg: "rgba(255, 255, 255, 0.75)",
};
const Page = styled.div`
  position: fixed;
  inset: 0;
  overflow-y: auto;
  /* The rings extend past the right edge; without this the browser resolves
     overflow-x to auto and the page scrolls sideways. */
  overflow-x: hidden;
  background: ${ink.page};
  /* The glow is the app's own accent at ~10% over white, per tokens.json.
     Expressing it as the accent means a sibling app inherits it for free. */
  background-image: radial-gradient(
    ellipse ${APP.glow.radiusX}px ${APP.glow.radiusY}px at ${APP.glow.atX}% 8%,
    ${({ theme }) => theme.colors.primary}1A 0%,
    ${({ theme }) => theme.colors.primary}00 58%
  );
  font-family: ${APP.font.family};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    background-image: radial-gradient(
      ellipse ${APP.glow.mobileRadiusX}px ${APP.glow.radiusY}px at
        ${APP.glow.atX}% 8%,
      ${({ theme }) => theme.colors.primary}1A 0%,
      ${({ theme }) => theme.colors.primary}00 58%
    );
  }
  color: ${ink.heading};
  /* Two concentric rings bleeding off the top-right, matching Figma's
     Ellipse 66 and 67. Positions come from APP.rings because Spocket and
     DropshipTool place them 109px further right than the other eight. Drawn as
     bordered circles rather than images so they stay crisp. */
  &::before,
  &::after {
    content: "";
    position: absolute;
    border-radius: 50%;
    pointer-events: none;
  }
  /* The two source SVGs carry DIFFERENT opacities — Ellipse 66 is 0.1, Ellipse
     67 is 0.05. Collapsing them to one value renders the outer ring twice as
     strong as designed. Hex alpha: 1A = 10%, 0D = 5%. */
  &::before {
    width: 793px;
    height: 793px;
    left: ${APP.rings.outer}%;
    top: -25.3%;
    border: 1px solid ${({ theme }) => theme.colors.primary}1A;
  }
  &::after {
    width: 905px;
    height: 905px;
    left: ${APP.rings.inner}%;
    top: -31.5%;
    border: 1px solid ${({ theme }) => theme.colors.primary}0D;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    /* Same circles, re-anchored for the 391px frame — not hidden. */
    &::before {
      left: ${APP.rings.outerMobile}%;
    }
    &::after {
      left: ${APP.rings.innerMobile}%;
    }
  }
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1332px;
  margin: 0 auto;
  min-height: 100%;
  padding: 54px 54px 40px;
  display: flex;
  flex-direction: column;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 28px 18px 32px;
  }
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const Logo = styled.img`
  height: ${APP.logo.height}px;
  width: ${APP.logo.width}px;
  /* Logo aspect ratios range from 3.33:1 to 6.32:1 across the ten apps, so a
     shared box would stretch most of them. contain letterboxes instead. */
  object-fit: contain;
  object-position: left center;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: ${APP.logo.mobileHeight}px;
    width: ${APP.logo.mobileWidth}px;
  }
`;

const Status = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 9.33px;
  padding: 7.13px 14.26px;
  border: 1.019px solid ${({ theme }) => theme.colors.primary}${APP.pillBorderAlpha};
  border-radius: 999px;
  background: ${ink.pillBg};
  font-size: 13.24px;
  letter-spacing: 0.265px;
  color: ${ink.muted};
  white-space: nowrap;

  &::before {
    content: "";
    width: 6.81px;
    height: 6.81px;
    border-radius: 3.4px;
    background: ${({ theme }) => theme.colors.primary};
    opacity: 0.833;
    flex: none;
  }
`;

const StatusFull = styled.span`
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const StatusShort = styled.span`
  display: none;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: inline;
  }
`;

const Main = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 80px 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 56px 0 40px;
    align-items: center;
    text-align: center;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 56px;
  line-height: 62px;
  font-weight: ${APP.font.headline};
  letter-spacing: -2.66px;
  white-space: nowrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    white-space: normal;
    text-wrap: balance;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.laptop}) {
    font-size: 42px;
    line-height: 52px;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.mobileM}) {
    font-size: 36px;
    line-height: 44px;
  }
`;

const Body = styled.p`
  margin: 22px 0 0;
  max-width: 640px;
  font-size: 18px;
  line-height: 30px;
  letter-spacing: 0.027px;
  color: ${ink.muted};
`;

const Cta = styled.a`
  margin-top: 22px;
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 15px 32px;
  border-radius: 8px;
  box-shadow: 0 10px 12px ${({ theme }) => theme.colors.primary}1F;
  background: ${({ theme }) => theme.colors.primary};
  color: #ffffff;
  font-size: 16px;
  font-weight: ${APP.font.cta};
  letter-spacing: 0.15px;
  text-decoration: none;
  transition: filter 0.15s ease;

  &:hover {
    filter: brightness(0.94);
  }
  &:focus-visible {
    outline: 2px solid ${ink.heading};
    outline-offset: 3px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    align-self: stretch;
    justify-content: center;
    margin-top: 22px;
    font-weight: 600;
  }
`;

const Reassurance = styled.p`
  margin: 18px 0 0;
  font-size: 14px;
  font-weight: ${APP.font.reassurance};
  color: ${ink.muted};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-weight: 400;
  }
`;

const Promo = styled.section`
  border-top: 1px solid ${ink.divider};
  padding-top: 26px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding-top: 32px;
  }
`;

const PromoCard = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px 40px;
  align-items: center;
  padding: 20px 24px;
  border: 1px solid ${ink.cardBorder};
  border-radius: 16px;
  background: ${ink.cardBg};

  @media (max-width: ${({ theme }) => theme.breakpoints.laptop}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const PromoLeft = styled.div`
  min-width: 210px;
`;

const PromoMiddle = styled.div`
  flex: 1 0 0;
  min-width: 260px;
`;

const PromoEyebrow = styled.p`
  margin: 0 0 5px;
  font-size: 11px;
  font-weight: ${APP.font.eyebrow};
  letter-spacing: 1.76px;
  text-transform: uppercase;
  color: ${ink.muted};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-weight: 400;
  }
`;

const PromoName = styled.p`
  margin: 0;
  font-size: 19px;
  font-weight: ${APP.font.promoName};
  letter-spacing: -0.285px;
`;

const PromoHeadline = styled.p`
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: ${APP.font.promoHeadline};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin: 0;
    line-height: 22px;
  }
`;

const PromoBlurb = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 21px;
  color: ${ink.muted};
`;

const PromoLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: ${APP.font.promoLink};
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: underline;
  text-underline-offset: 3px;
  white-space: nowrap;

  &:focus-visible {
    outline: 2px solid ${ink.heading};
    outline-offset: 3px;
  }
`;

const ArrowLeft = () => (
  <svg
    width="24"
    height="12"
    viewBox="0 0 24 12"
    fill="none"
    aria-hidden="true">
    <path
      d="M16.945 0.2432C16.8676 0.3197 16.8061 0.4107 16.7642 0.5109C16.7222 0.6112 16.7007 0.7187 16.7007 0.8273C16.7007 0.9359 16.7222 1.0434 16.7642 1.1437C16.8061 1.2439 16.8676 1.3349 16.945 1.4114L20.7444 5.1957H1.30123C1.08217 5.1957 0.87208 5.2824 0.71718 5.4367C0.56228 5.5909 0.47526 5.8002 0.47526 6.0184C0.47526 6.2366 0.56228 6.4458 0.71718 6.6001C0.87208 6.7544 1.08217 6.841 1.30123 6.841H20.7279L16.945 10.6007C16.7911 10.7548 16.7048 10.9633 16.7048 11.1806C16.7048 11.398 16.7911 11.6065 16.945 11.7606C17.0997 11.9138 17.3091 11.9999 17.5273 11.9999C17.7455 11.9999 17.9548 11.9138 18.1096 11.7606L23.3628 6.5284C23.4334 6.4609 23.4897 6.3798 23.5281 6.29C23.5666 6.2002 23.5864 6.1036 23.5864 6.006C23.5864 5.9084 23.5666 5.8118 23.5281 5.7221C23.4897 5.6323 23.4334 5.5512 23.3628 5.4836L18.1179 0.2432C18.0411 0.1661 17.9497 0.1049 17.8491 0.0631C17.7484 0.0214 17.6404 -0.0001 17.5315 -0.0001C17.4224 -0.0001 17.3144 0.0214 17.2138 0.0631C17.1132 0.1049 17.0218 0.1661 16.945 0.2432Z"
      fill="currentColor"
      transform="rotate(180 12 6)"
    />
  </svg>
);

const ArrowOut = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    style={{ transform: "rotate(-90deg)" }}>
    <path
      d="M10.6369 4.5666C10.5774 4.5705 10.5173 4.5862 10.4602 4.6127C10.403 4.6392 10.3498 4.676 10.3038 4.7209C10.2577 4.7659 10.2196 4.8181 10.1917 4.8746C10.1638 4.9311 10.1467 4.9908 10.1413 5.0501L9.86284 7.9765L3.07252 1.0171C2.99602 0.9386 2.88588 0.8993 2.76633 0.9077C2.64679 0.9162 2.52763 0.9716 2.43507 1.0619C2.34251 1.1522 2.28413 1.27 2.27278 1.3893C2.26143 1.5086 2.29803 1.6197 2.37454 1.6981L9.15908 8.6516L6.24303 8.8537C6.12392 8.8624 6.00532 8.9178 5.91312 9.0078C5.82091 9.0977 5.76261 9.2149 5.75096 9.3338C5.74001 9.4526 5.77662 9.5631 5.85283 9.6412C5.92904 9.7194 6.03864 9.7587 6.15769 9.7506L10.2119 9.4653C10.2652 9.4626 10.3193 9.4492 10.3708 9.4258C10.4223 9.4024 10.4702 9.3695 10.5116 9.3291C10.553 9.2887 10.5871 9.2416 10.6117 9.1907C10.6364 9.1398 10.6511 9.0861 10.6551 9.0328L11.0465 4.9864C11.0524 4.927 11.0464 4.869 11.029 4.8157C11.0116 4.7624 10.983 4.7148 10.9449 4.6758C10.9068 4.6368 10.86 4.607 10.8071 4.5883C10.7543 4.5696 10.6964 4.5622 10.6369 4.5666Z"
      fill="currentColor"
    />
  </svg>
);

export const MaintenancePage = () => (
  <Page role="alert">
    <Inner>
      <Header>
        <Logo src={APP.logo.src} alt={APP.name} />
        <Status>
          <StatusFull>{SHARED.status}</StatusFull>
          <StatusShort>{SHARED.statusShort}</StatusShort>
        </Status>
      </Header>

      <Main>
        <Title>{APP.name} will be back soon.</Title>
        <Body>{SHARED.body}</Body>
        {/* A plain anchor, not a router link: the router may be exactly what
            is unavailable, and a full navigation is the intent anyway. */}
        <Cta href={APP.home}>
          <ArrowLeft />
          {SHARED.cta}
        </Cta>
        <Reassurance>{SHARED.reassurance}</Reassurance>
      </Main>

      <Promo>
        <PromoCard>
          <PromoLeft>
            <PromoEyebrow>{SHARED.promoEyebrow}</PromoEyebrow>
            <PromoName>{APP.promo.name}</PromoName>
          </PromoLeft>
          <PromoMiddle>
            <PromoHeadline>{APP.promo.headline}</PromoHeadline>
            <PromoBlurb>{APP.promo.blurb}</PromoBlurb>
          </PromoMiddle>
          <PromoLink
            href={APP.promo.linkUrl}
            target="_blank"
            rel="noopener noreferrer">
            {APP.promo.linkLabel}
            <ArrowOut />
          </PromoLink>
        </PromoCard>
      </Promo>
    </Inner>
  </Page>
);

export default MaintenancePage;
